import { Target, Eye, HeartHandshake, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { SectionHeading } from "@/components/SectionHeading";
import { AppointmentCTA } from "@/components/AppointmentCTA";
import { Icon } from "@/utils/icons";
import { SITE } from "@/config/site";
import { WHY_CHOOSE_US } from "@/data/whyChooseUs";

const VALUES = [
  { icon: Target, title: "Our Mission", text: "To make dependable, compassionate healthcare accessible to every family in Kasdol and nearby communities." },
  { icon: Eye, title: "Our Vision", text: "To be the most trusted healthcare destination in the region — known for honesty, quality and patient-first care." },
  { icon: HeartHandshake, title: "Our Approach", text: "We listen first, explain clearly and involve patients in every decision about their health." },
];

const LOCAL_POINTS = [
  "Located in Kasdol, serving surrounding villages and towns",
  "Care in a familiar, comfortable local setting",
  "Easy appointment requests on WhatsApp — no long queues",
  "Clear guidance on follow-ups and referrals when needed",
];

export default function About() {
  return (
    <>
      <Seo
        title={`About Us — ${SITE.name}`}
        siteName={SITE.name}
        description={`Learn about ${SITE.name}, Kasdol — our mission, vision and patient-first approach to healthcare in Chhattisgarh.`}
      />
      <PageHeader
        title="About Us"
        description={`${SITE.tagline} — serving Kasdol and nearby communities with dependable, compassionate care.`}
        crumbs={[{ label: "About" }]}
      />

      <section className="py-16 sm:py-20 lg:py-24" data-testid="about-intro-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Who We Are"
              title="A hospital built around its community"
              description={`${SITE.name} was established to give the people of Kasdol access to quality medical care without travelling far from home. We combine qualified doctors, a clean clinical environment and honest communication.`}
            />
            <ul className="-mt-4 space-y-3">
              {LOCAL_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-3 w-3" aria-hidden="true" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid gap-4">
            <img src="/images/hospital-hallway.jpg" alt="Clean hospital corridor" loading="lazy" className="w-full rounded-3xl object-cover shadow-lg" />
            <img src="/images/patient-care.jpg" alt="Doctor caring for a patient" loading="lazy" className="w-full rounded-3xl object-cover shadow-lg" />
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="about-values-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Mission & Vision" title="What guides us every day" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {VALUES.map(({ icon: I, title, text }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-slate-50 p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`about-value-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-navy-800 text-white">
                  <I className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy-900 py-16 sm:py-20 lg:py-24" data-testid="about-why-us-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading light eyebrow="Why Choose Us" title="Care you can feel confident about" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE_US.map((w) => (
              <div key={w.title} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-400">
                  <Icon name={w.icon} className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-white">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{w.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AppointmentCTA />
    </>
  );
}
